import { ReactElement, useRef, useEffect, useMemo, useState } from 'react';
import { GoogleMapsProps } from './GoogleMaps.types';
import { useGoogleMapsLoader } from './GoogleMapsLoader';
import {
  addMarkersToPanorama,
  orientPanoramaToNearest,
  createPanorama,
  attachPanoramaListeners,
  cleanupPanorama,
} from './GoogleMapsStreetView.utils';

const GoogleMapsStreetViewComponent = ({ center, markerData, onPositionChange }: GoogleMapsProps): ReactElement => {
  const streetViewRef = useRef<google.maps.StreetViewPanorama | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const streetViewContainerRef = useRef<HTMLDivElement | null>(null);
  const [isPanoramaReady, setIsPanoramaReady] = useState(false);
  const hasOrientedRef = useRef(false);
  const onPositionChangeRef = useRef(onPositionChange);
  onPositionChangeRef.current = onPositionChange;

  const { isLoaded } = useGoogleMapsLoader();

  const markerDataKey = useMemo(
    () => (markerData ? JSON.stringify(markerData.map((marker) => [marker.lat, marker.lng])) : ''),
    [markerData],
  );

  useEffect(() => {
    if (!isLoaded || !center || !streetViewContainerRef.current) return undefined;

    const container = streetViewContainerRef.current;
    setIsPanoramaReady(false);
    hasOrientedRef.current = false;

    const panorama = createPanorama(container, center);
    streetViewRef.current = panorama;

    const positionListener = attachPanoramaListeners(panorama, setIsPanoramaReady, onPositionChangeRef);

    return () => {
      setIsPanoramaReady(false);
      cleanupPanorama(panorama, positionListener, markersRef, container);
      streetViewRef.current = null;
    };
  }, [isLoaded, center?.lat, center?.lng]);

  useEffect(() => {
    if (!isPanoramaReady || !streetViewRef.current || !streetViewContainerRef.current) return;

    if (!hasOrientedRef.current) {
      orientPanoramaToNearest(streetViewRef.current, markerData, center!);
      hasOrientedRef.current = true;
    }

    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    addMarkersToPanorama(streetViewRef.current, markerData, center!, markersRef, streetViewContainerRef.current);
  }, [markerDataKey, isPanoramaReady]);

  if (!isLoaded) {
    return <div style={{ height: '100%', width: '100%' }} />;
  }

  return <div ref={streetViewContainerRef} style={{ height: '100%', width: '100%' }} />;
};

export const GoogleMapsStreetView = GoogleMapsStreetViewComponent;
